import { CpcHorizon, CpcVariable } from '../../types';

export class CpcOutlookPanel {
  public render(state: any): string {
    const horizon: CpcHorizon = state.cpcHorizon || '6_10';
    const variable: CpcVariable = state.cpcVariable || 'temp';

    const tempLegend = `
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <div style="font-size: 0.72rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">
          Temperature Anomaly Probabilities
        </div>

        <!-- Continuous-look gradient segments -->
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <!-- Much Above Normal -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(153, 0, 0, 0.15); border-left: 3px solid #cc0000; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #cc0000;"></span>
              <span style="font-size: 0.76rem; font-weight: 700; color: #fca5a5;">Above Normal</span>
            </div>
            <span style="font-size: 0.70rem; color: #cbd5e1; font-weight: 600;">50% – 80%+</span>
          </div>

          <!-- Moderately Above -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(255, 128, 128, 0.12); border-left: 3px solid #ff8080; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #ff8080;"></span>
              <span style="font-size: 0.76rem; font-weight: 600; color: #fecaca;">Leaning Above</span>
            </div>
            <span style="font-size: 0.70rem; color: #94a3b8;">33% – 50%</span>
          </div>

          <!-- Near Normal / EC -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(191, 223, 191, 0.12); border-left: 3px solid #bfdfbf; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #bfdfbf;"></span>
              <span style="font-size: 0.76rem; font-weight: 600; color: #dcfce7;">Near Normal / Equal</span>
            </div>
            <span style="font-size: 0.70rem; color: #94a3b8;">~33% Climatology</span>
          </div>

          <!-- Moderately Below -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(121, 166, 210, 0.12); border-left: 3px solid #79a6d2; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #79a6d2;"></span>
              <span style="font-size: 0.76rem; font-weight: 600; color: #bae6fd;">Leaning Below</span>
            </div>
            <span style="font-size: 0.70rem; color: #94a3b8;">33% – 50%</span>
          </div>

          <!-- Much Below Normal -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(19, 57, 128, 0.15); border-left: 3px solid #133980; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #133980;"></span>
              <span style="font-size: 0.76rem; font-weight: 700; color: #93c5fd;">Below Normal</span>
            </div>
            <span style="font-size: 0.70rem; color: #cbd5e1; font-weight: 600;">50% – 80%+</span>
          </div>
        </div>
      </div>
    `;

    const precipLegend = `
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <div style="font-size: 0.72rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">
          Precipitation Anomaly Probabilities
        </div>

        <div style="display: flex; flex-direction: column; gap: 4px;">
          <!-- Much Wetter / Above Normal -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(0, 120, 20, 0.16); border-left: 3px solid #009620; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #007814;"></span>
              <span style="font-size: 0.76rem; font-weight: 700; color: #86efac;">Above Normal (Wetter)</span>
            </div>
            <span style="font-size: 0.70rem; color: #cbd5e1; font-weight: 600;">50% – 80%+</span>
          </div>

          <!-- Moderately Wetter -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(149, 206, 127, 0.14); border-left: 3px solid #95ce7f; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #95ce7f;"></span>
              <span style="font-size: 0.76rem; font-weight: 600; color: #bbf7d0;">Leaning Wetter</span>
            </div>
            <span style="font-size: 0.70rem; color: #94a3b8;">33% – 50%</span>
          </div>

          <!-- Near Normal / EC -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(160, 160, 160, 0.12); border-left: 3px solid #a0a0a0; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #a0a0a0;"></span>
              <span style="font-size: 0.76rem; font-weight: 600; color: #f1f5f9;">Near Normal / Equal</span>
            </div>
            <span style="font-size: 0.70rem; color: #94a3b8;">~33% Climatology</span>
          </div>

          <!-- Moderately Drier -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(216, 167, 79, 0.14); border-left: 3px solid #d8a74f; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #d8a74f;"></span>
              <span style="font-size: 0.76rem; font-weight: 600; color: #fde047;">Leaning Drier</span>
            </div>
            <span style="font-size: 0.70rem; color: #94a3b8;">33% – 50%</span>
          </div>

          <!-- Much Drier / Below Normal -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: rgba(187, 109, 51, 0.16); border-left: 3px solid #bb6d33; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #804000;"></span>
              <span style="font-size: 0.76rem; font-weight: 700; color: #fed7aa;">Below Normal (Drier)</span>
            </div>
            <span style="font-size: 0.70rem; color: #cbd5e1; font-weight: 600;">50% – 80%+</span>
          </div>
        </div>
      </div>
    `;

    return `
      <div style="display: flex; flex-direction: column; gap: 10px; font-family: 'Plus Jakarta Sans', system-ui, sans-serif;">
        <!-- Horizon Selector Tabs -->
        <div style="display: flex; background: rgba(15, 23, 42, 0.6); padding: 3px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08);">
          <button
            type="button"
            onclick="window.setCpcHorizon('6_10')"
            style="flex: 1; padding: 5px 6px; font-size: 0.74rem; font-weight: 600; border: none; border-radius: 6px; cursor: pointer; transition: all 0.15s ease; ${
              horizon === '6_10'
                ? 'background: #0284c7; color: #ffffff; box-shadow: 0 2px 8px rgba(2, 132, 199, 0.4);'
                : 'background: transparent; color: #94a3b8;'
            }"
          >
            6–10 Day
          </button>
          <button
            type="button"
            onclick="window.setCpcHorizon('8_14')"
            style="flex: 1; padding: 5px 6px; font-size: 0.74rem; font-weight: 600; border: none; border-radius: 6px; cursor: pointer; transition: all 0.15s ease; ${
              horizon === '8_14'
                ? 'background: #0284c7; color: #ffffff; box-shadow: 0 2px 8px rgba(2, 132, 199, 0.4);'
                : 'background: transparent; color: #94a3b8;'
            }"
          >
            8–14 Day
          </button>
          <button
            type="button"
            onclick="window.setCpcHorizon('monthly')"
            style="flex: 1; padding: 5px 6px; font-size: 0.74rem; font-weight: 600; border: none; border-radius: 6px; cursor: pointer; transition: all 0.15s ease; ${
              horizon === 'monthly'
                ? 'background: #0284c7; color: #ffffff; box-shadow: 0 2px 8px rgba(2, 132, 199, 0.4);'
                : 'background: transparent; color: #94a3b8;'
            }"
          >
            Monthly
          </button>
          <button
            type="button"
            onclick="window.setCpcHorizon('three_month')"
            style="flex: 1; padding: 5px 6px; font-size: 0.74rem; font-weight: 600; border: none; border-radius: 6px; cursor: pointer; transition: all 0.15s ease; ${
              horizon === 'three_month'
                ? 'background: #0284c7; color: #ffffff; box-shadow: 0 2px 8px rgba(2, 132, 199, 0.4);'
                : 'background: transparent; color: #94a3b8;'
            }"
          >
            3-Month
          </button>
        </div>

        <!-- Variable Selector (Temp vs Precip) -->
        <div style="display: flex; background: rgba(15, 23, 42, 0.4); padding: 2px; border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.05);">
          <button
            type="button"
            onclick="window.setCpcVariable('temp')"
            style="flex: 1; padding: 4px 6px; font-size: 0.72rem; font-weight: 600; border: none; border-radius: 5px; cursor: pointer; transition: all 0.15s ease; ${
              variable === 'temp'
                ? 'background: rgba(239, 68, 68, 0.25); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.4);'
                : 'background: transparent; color: #94a3b8;'
            }"
          >
            🌡️ Temperature
          </button>
          <button
            type="button"
            onclick="window.setCpcVariable('precip')"
            style="flex: 1; padding: 4px 6px; font-size: 0.72rem; font-weight: 600; border: none; border-radius: 5px; cursor: pointer; transition: all 0.15s ease; ${
              variable === 'precip'
                ? 'background: rgba(14, 165, 233, 0.25); color: #38bdf8; border: 1px solid rgba(14, 165, 233, 0.4);'
                : 'background: transparent; color: #94a3b8;'
            }"
          >
            🌧️ Precipitation
          </button>
        </div>

        <!-- Legend Swatches -->
        ${variable === 'temp' ? tempLegend : precipLegend}

        <!-- Footer Caption -->
        <div style="display: flex; align-items: center; justify-content: space-between; font-size: 0.68rem; color: #64748b; margin-top: 2px;">
          <span>Source: NOAA Climate Prediction Center</span>
          <span>Click map for %</span>
        </div>
      </div>
    `;
  }
}

export default CpcOutlookPanel;
